import { ChunkReloader } from "@/components/chunk-reloader";
import { envClient } from "@/configs/env-client.config.ts";
import * as Sentry from "@sentry/react";
import "antd/dist/reset.css";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import { ReactQueryProvider } from "./libs/react-query/react-query-provider";
import { createRoutesFromFiles } from "./libs/react-router";
import { ValidationError } from "./utils/validation-error";
import "./middleware.ts";

Sentry.init({
  dsn: envClient.VITE_SENTRY_DSN,
  environment: envClient.VITE_SENTRY_ENVIRONMENT,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,

  // Performance Monitoring
  tracesSampleRate: envClient.VITE_SENTRY_TRACE_SAMPLE_RATE,

  // Session Replay
  integrations: [
    Sentry.replayIntegration({
      // Privacy configuration
      maskAllText: envClient.VITE_SENTRY_REPLAY_MASK_ALL_TEXT || false, // Mask all text content
      maskAllInputs: envClient.VITE_SENTRY_REPLAY_MASK_ALL_INPUTS ?? true, // Mask all input fields
      blockAllMedia: envClient.VITE_SENTRY_REPLAY_BLOCK_ALL_MEDIA || false, // Block all media elements

      // Network configuration
      networkDetailAllowUrls: [window.location.origin], // Only capture network details for your domain
      networkCaptureBodies: true, // Capture request and response bodies
      networkRequestHeaders: [], // Don't capture request headers by default
      networkResponseHeaders: [], // Don't capture response headers by default
    }),
  ],

  // Replay sampling configuration
  replaysSessionSampleRate: envClient.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE || 0.1, // 10% of sessions
  replaysOnErrorSampleRate: envClient.VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE || 1.0, // 100% of sessions with errors
  beforeSend(event, hint) {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
    const message = event.exception?.values?.[0].value || "";
    const type = event.exception?.values?.[0].type || "";

    // Ignore error from custom validation FE
    const isValidationError = frames.some((frame) =>
      frame.filename?.includes("src/utils/validation.ts")
    );

    if (isValidationError) {
      return null;
    }

    const error = hint.originalException;

    // Validation already shown to the user; thrown only to abort the action.
    if (error instanceof ValidationError) {
      return null;
    }

    // Request never reached the server (offline, VPN, server unreachable). User gets a toast from the fetcher.
    if (
      error instanceof TypeError &&
      /^(Failed to fetch|NetworkError when attempting to fetch resource|Load failed)/.test(error.message)
    ) {
      return null;
    }

    // Ignore from server in
    if (error instanceof Error) {
      try {
        const parsed = JSON.parse(error.message);

        if (
          typeof parsed.status_code === "number" &&
          parsed.status_code >= 400 &&
          parsed.status_code < 500
        ) {
          return null;
        }
      } catch { }
    }

    // Drop AntD validation rejections (plain object with these keys)
    if (
      error &&
      typeof error === "object" &&
      "errorFields" in error &&
      "values" in error
    ) {
      return null;
    }

    //Error Extension Browser
    if (event.exception?.values) {
      if (
        message.includes("Extension") ||
        message.includes("chrome-extension") ||
        type.includes("ExtensionError")
      ) {
        return null; // jangan kirim ke Sentry
      }
    }

    //Error Extension Browser
    if (frames.some((f) => f.filename?.includes("chrome-extension"))) {
      return null;
    }

    // Ignore chunk/dynamic import errors (caused by deployments, auto-recovered via ChunkReloader)
    if (
      message.includes("dynamically imported module") ||
      message.includes("ChunkLoadError") ||
      message.includes("Loading chunk") ||
      message.includes("Loading CSS chunk") ||
      message.includes("Unable to preload CSS") ||
      message.includes("Importing a module script failed")
    ) {
      return null;
    }

    return event;
  },
});

const pageFiles = import.meta.glob("./app/**/*(page|layout).tsx");
const errorFiles = import.meta.glob("./app/**/*error.tsx");
const notFoundFiles = import.meta.glob("./app/**/*404.tsx");
const loadingFiles = import.meta.glob("./app/**/*loading.tsx");

const routes = createRoutesFromFiles(pageFiles, errorFiles, notFoundFiles, loadingFiles);
const router = createBrowserRouter([routes]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ChunkReloader />
    <ReactQueryProvider>
      <RouterProvider router={router} />
    </ReactQueryProvider>
  </StrictMode>,
);
